import { useState, useEffect, useRef, useCallback } from 'react';
import { onSnapshot, doc } from 'firebase/firestore';
import { db } from '../firebase';
import { ref, deleteObject } from 'firebase/storage';
import { storage } from '../firebase';
import { dataService } from '../services/dataService';

export const usePermitLogic = (permit, onClose, previewMode = false) => {
    // --- STATE ---
    const [checklist, setChecklist] = useState(permit.executionChecklist || {});
    const [saving, setSaving] = useState(false);
    const [status, setStatus] = useState(permit.status || 'approved');
    const [conditionScore, setConditionScore] = useState(permit.conditionScore || null);
    const [isSynced, setIsSynced] = useState(true);
    const [isOnline, setIsOnline] = useState(typeof window !== 'undefined' ? window.navigator.onLine : true);

    // Completion Logic State
    const [showCompletionModal, setShowCompletionModal] = useState(false);
    const [completionFeedback, setCompletionFeedback] = useState(permit.completionFeedback || {});
    const [isReplacementConfirmed, setIsReplacementConfirmed] = useState(false);
    const [instructionAnswer, setInstructionAnswer] = useState(null);
    const [instructionFeedback, setInstructionFeedback] = useState('');
    const [completionChecks, setCompletionChecks] = useState({
        ultimoIngevuld: false,
        werkplekSchoon: false,
        onderdelenAfgeboekt: false,
    });

    // --- SESSION PHOTO TRACKING (For cleanup on cancel) ---
    const [executionSessionUrls, setExecutionSessionUrls] = useState([]);

    const timeoutRef = useRef(null);

    // --- EFFECTS ---

    // Real-time Sync Hub
    useEffect(() => {
        if (previewMode || !permit.id) return;

        const docRef = doc(db, 'permits', permit.id);
        const unsubscribe = onSnapshot(docRef, { includeMetadataChanges: true }, (snapshot) => {
            if (snapshot.exists()) {
                const data = snapshot.data();
                // Optimistic check: only update if remote data is newer or different and we are NOT currently typing/saving
                // But specifically for metadata:
                setIsSynced(!snapshot.metadata.hasPendingWrites);
            }
        });

        const handleOffline = () => setIsOnline(false);
        const handleOnline = () => setIsOnline(true);
        window.addEventListener('offline', handleOffline);
        window.addEventListener('online', handleOnline);

        return () => {
            unsubscribe();
            window.removeEventListener('offline', handleOffline);
            window.removeEventListener('online', handleOnline);
        };
    }, [permit.id, previewMode]);

    // --- SESSION CLEANUP FUNCTION ---
    const cleanupExecutionSession = useCallback(async () => {
        if (executionSessionUrls.length === 0) {
            return;
        }
        try {
            await Promise.all(executionSessionUrls.map(async (url) => {
                try {
                    const storageRef = ref(storage, url);
                    await deleteObject(storageRef);
                } catch (e) {
                    console.warn(`Failed to delete ${url}`, e);
                }
            }));
        } catch (err) {
            console.error("[Execution] Cleanup failed:", err);
        }
        setExecutionSessionUrls([]);
    }, [executionSessionUrls]);

    // --- ADD TO SESSION ---
    const addExecutionSessionUrl = useCallback((url) => {
        setExecutionSessionUrls(prev => [...prev, url]);
    }, []);

    // --- BEFOREUNLOAD WARNING ---
    useEffect(() => {
        if (previewMode) return;

        const handleBeforeUnload = (e) => {
            if (executionSessionUrls.length > 0) {
                e.preventDefault();
                e.returnValue = '';
            }
        };

        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, [executionSessionUrls, previewMode]);

    // Initialize/Sync mechanic checks INTO MAIN CHECKLIST (Legacy/Compat Logic)
    useEffect(() => {
        if (permit.checks && permit.checks.length > 0) {
            setChecklist(prev => {
                const newChecklist = { ...prev };
                let changed = false;
                permit.checks.forEach((_, idx) => {
                    const key = `mechanic_check_${idx}`;
                    if (newChecklist[key] === undefined) {
                        newChecklist[key] = false;
                        changed = true;
                    }
                });
                return changed ? newChecklist : prev;
            });
        }
    }, [permit.checks]);

    // --- COMPUTED VALUES ---

    const allLotoRemoved = (permit.lotoSteps || []).every((_, idx) => completionChecks[`loto_remove_${idx}`]);
    const allMechanicChecks = (permit.checks || []).every((_, idx) => checklist[`mechanic_check_${idx}`]);

    // If user says instruction was wrong, they MUST explain what was wrong
    const instructionValid = instructionAnswer === 'yes' ||
        (instructionAnswer === 'no' && instructionFeedback.trim().length > 3);

    // Prioritized Validation Reason
    const getIncompletionReason = () => {
        if (!allMechanicChecks) return "Vink alle werkstappen af";
        if (permit.lotoRequired && !allLotoRemoved) return "Vink alle LOTO-vrijgaven af";
        if (!completionChecks.ultimoIngevuld) return "Bevestig Ultimo-registratie";
        if (!completionChecks.werkplekSchoon) return "Bevestig schone werkplek";
        if (!completionChecks.onderdelenAfgeboekt) return "Bevestig afboeken onderdelen";
        if (!instructionValid) return "Geef feedback op instructie";
        if (conditionScore === null) return "Geef conditiescore (1-6) op";
        if ((completionFeedback?.feedbackText?.trim()?.length || 0) <= 5) return "Vul toelichting in (min. 5 tekens)";
        return null;
    };

    const canCompleteReason = getIncompletionReason();
    const canComplete = canCompleteReason === null;

    const totalChecks = (permit.risks?.length || 0) + (permit.lotoSteps?.length || 0);
    const checkedCount = Object.keys(checklist).filter(k =>
        (k.startsWith('risk_') || k.startsWith('loto_')) && checklist[k]
    ).length;
    const progress = totalChecks > 0 ? Math.round((checkedCount / totalChecks) * 100) : 0;


    // --- HANDLERS ---

    const handleChecklistUpdate = (newChecklist) => {
        setChecklist(newChecklist);

        if (!previewMode) {
            setSaving(true);
            if (timeoutRef.current) clearTimeout(timeoutRef.current);
            timeoutRef.current = setTimeout(async () => {
                try {
                    await dataService.updatePermitChecklist(permit.id, newChecklist);
                } catch (err) {
                    console.error("Auto-save failed:", err);
                } finally {
                    setSaving(false);
                }
            }, 1000);
        }
    };

    const handleLotoDeviation = async (url, note) => {
        if (!note || note.trim().length === 0) return;
        if (previewMode) return;

        try {
            const lotoMeta = {
                [url]: {
                    discrepancyNote: note,
                    hasDiscrepancy: true,
                    matchesDrawing: false,
                    reportedAt: new Date().toISOString(),
                    reportedBy: permit.executor || 'Onbekend',
                    status: 'open'
                }
            };
            await dataService.saveLotoMetadata(permit.id, lotoMeta);
        } catch (err) {
            console.error("Failed to save LOTO deviation:", err);
        }
    };

    const handleStatusUpdate = async (newStatus) => {
        if (newStatus === 'completed') {
            setShowCompletionModal(true);
            // Scroll logic is UI specific, handle in Component
            return 'open_modal';
        }

        if (previewMode) {
            alert("Dit is een voorbeeldweergave. U kunt de status niet wijzigen.");
            return;
        }

        if (!window.confirm(`Status wijzigen naar: ${newStatus === 'completed' ? 'KLAAR GEMELD' : 'BEZIG'}?`)) return;

        try {
            await dataService.updatePermitStatus(permit.id, newStatus);
            setStatus(newStatus);
            alert("Status bijgewerkt!");
            onClose();
        } catch (err) {
            alert("Fout bij updaten status: " + err.message);
        }
        return 'updated';
    };

    const handleFinalCompletion = async () => {
        if (previewMode) {
            alert("Dit is een voorbeeldweergave. U kunt niet afronden.");
            return;
        }

        try {
            let finalFeedback = completionFeedback;
            if (instructionAnswer === 'no' && instructionFeedback) {
                finalFeedback = `[Instructie Feedback: ${instructionFeedback}]\n\n` + finalFeedback;
            }

            // MJOP Logic
            const meta = permit.taskMetadata || permit.task_metadata || {};
            let finalCondition = conditionScore;
            let replacementData = null;

            if (meta.isReplacement) {
                if (isReplacementConfirmed) {
                    finalCondition = 1;
                    replacementData = {
                        newBuildYear: new Date().getFullYear(),
                        isReplacement: true
                    };
                }
            }

            await dataService.updatePermitStatus(
                permit.id,
                'completed',
                finalFeedback,
                '',
                null,
                finalCondition,
                replacementData
            );

            // Clear session uploads to prevent cleanup from deleting committed photos
            setExecutionSessionUrls([]);

            setStatus('completed');
            setShowCompletionModal(false);

            if (replacementData) {
                alert("✅ Taak afgemeld & Asset Data bijgewerkt (Bouwjaar: " + replacementData.newBuildYear + ")");
            } else {
                alert("Taak succesvol afgemeld!");
            }

            onClose();
        } catch (err) {
            alert("Fout bij afmelden: " + err.message);
        }
    };

    return {
        // State
        checklist,
        saving,
        status,
        conditionScore,
        setConditionScore,
        canCompleteReason,
        isReplacementConfirmed,
        setIsReplacementConfirmed,
        isSynced,
        isOnline,
        showCompletionModal, setShowCompletionModal,
        completionFeedback, setCompletionFeedback,
        instructionAnswer, setInstructionAnswer,
        instructionFeedback, setInstructionFeedback,
        completionChecks, setCompletionChecks,
        // Computed
        canComplete,
        progress,
        // Session Management
        executionSessionUrls,
        addExecutionSessionUrl,
        cleanupExecutionSession,
        // Actions
        handleChecklistUpdate,
        handleLotoDeviation,
        handleStatusUpdate,
        handleFinalCompletion
    };
};
